"use client";

import React, { useState, useEffect } from "react";
import {
  Card,
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Stack,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";

const ClientCredentials = ({
  email,
  loginId,
  credentialsReady,
  isEditMode = false,
  initialEnableLogin = true,
  onCreateClient,
  onSaveChanges,
  onResendOnboarding,
  onToggleLogin
}) => {
  const [enableClientLogin, setEnableClientLogin] = useState(initialEnableLogin);

  useEffect(() => {
    if (isEditMode) {
      setEnableClientLogin(initialEnableLogin);
    }
  }, [isEditMode, initialEnableLogin]);

  const clientPortalURL = "https://client.askbud-i.com";

  const handleCreateClientAndSend = () => {
    if (onCreateClient) {
      onCreateClient(enableClientLogin);
    }
  };

  const handleSaveChanges = () => {
    if (onSaveChanges) {
      onSaveChanges({ enableClientLogin });
    }
  };

  const handleResendOnboarding = () => {
    if (onResendOnboarding) {
      onResendOnboarding();
    }
  };

  const handleToggleLogin = async (event) => {
    const newValue = event.target.checked;
    setEnableClientLogin(newValue);

    // In edit mode, trigger API call
    if (isEditMode && onToggleLogin) {
      onToggleLogin(newValue);
    }
  };

  return (
    <Box component="form">
      <Card
        sx={{
          boxShadow: "none",
          borderRadius: "7px",
          mb: "25px",
          padding: { xs: "18px", sm: "20px", lg: "25px" },
        }}
        className="rmui-card"
      >
        {/* Client Portal Login URL */}
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth>
            <Typography
              component="label"
              sx={{ fontWeight: "500", fontSize: "14px", mb: "10px" }}
            >
              Client Portal Login URL
            </Typography>
            <TextField
              value={clientPortalURL}
              placeholder={isEditMode ? "Loading..." : "URL will be generated after client creation"}
              InputProps={{ readOnly: true }}
              variant="filled"
              sx={{
                "& .MuiInputBase-root": {
                  border: "1px solid #D5D9E2",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "7px",
                },
                "& .MuiInputBase-root::before": { border: "none" },
                "& .MuiInputBase-root:hover::before": { border: "none" },
              }}
            />
          </FormControl>
        </Box>

        {/* Enable Client Login Switch */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enableClientLogin}
                onChange={handleToggleLogin}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#605DFF",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#605DFF",
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
                Enable Client Login
              </Typography>
            }
          />
        </Box>

        {/* Action Buttons */}
        {isEditMode ? (
          // Edit Mode: Only Resend Onboarding Link (Switch triggers API directly)
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={handleResendOnboarding}
              disabled={!email}
              sx={{
                textTransform: "none",
                borderRadius: "7px",
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: "500",
                borderColor: "#605DFF",
                color: "#605DFF",
                "&:hover": {
                  borderColor: "#3e2ad8",
                  backgroundColor: "#ECF0FF",
                },
              }}
            >
              Resend Onboarding Link
            </Button>
          </Box>
        ) : (
          // Create Mode: Create Client & Send Onboarding Link
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EmailIcon />}
              onClick={handleCreateClientAndSend}
              disabled={!email}
              sx={{
                textTransform: "none",
                borderRadius: "7px",
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: "500",
                backgroundColor: "#605DFF",
                "&:hover": {
                  backgroundColor: "#3e2ad8",
                },
                "&:disabled": {
                  backgroundColor: "#B1BBC8",
                  color: "#fff",
                },
              }}
            >
              Create Client & Send Onboarding Link
            </Button>
            {!email && (
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 1, color: "#64748B" }}
              >
                Please complete Client Information first
              </Typography>
            )}
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default ClientCredentials;
